import { Link } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Helmet } from '../components/Helmet';

const content = `
# Politique de confidentialité

Dernière mise à jour : 29 juin 2026

La présente politique de confidentialité explique comment Upnov IA collecte, utilise et protège les données personnelles transmises via le site [www.upnov.com](https://www.upnov.com).

## 1. Responsable du traitement

Le responsable du traitement est :

Cyril Cohen Solal
Nom commercial : Upnov IA
Adresse : 84 avenue Secrétan, 75019 Paris, France
SIRET : 93946193500016
Contact données personnelles : [dpo@upnov.com](mailto:dpo@upnov.com)

## 2. Données personnelles collectées

Upnov IA peut collecter les données personnelles suivantes lorsque vous utilisez le site :

* nom et prénom
* adresse email
* numéro de téléphone
* nom de l'entreprise
* fonction ou poste occupé
* contenu du message envoyé
* informations transmises dans le cadre d'une demande de contact, de diagnostic gratuit, d'information, de programme ou de devis
* données techniques liées à la navigation sur le site, notamment via les cookies et outils de mesure d'audience

Upnov IA ne collecte que les données nécessaires au traitement des demandes reçues.

## 3. Finalités du traitement

Les données collectées peuvent être utilisées pour :

* répondre aux demandes envoyées via les formulaires du site
* proposer un diagnostic IA ou un échange commercial
* préparer et transmettre un devis personnalisé
* envoyer des informations sur les formations, conférences, ateliers ou prestations proposées par Upnov IA
* assurer le suivi commercial des prospects et clients
* améliorer le fonctionnement du site
* mesurer l'audience et analyser l'utilisation du site
* respecter les obligations légales, comptables ou administratives applicables

## 4. Formulaires du site

Le site peut proposer plusieurs formulaires, notamment :

* formulaire de contact
* demande de diagnostic gratuit
* demande d'information
* demande de programme
* demande de devis

Les informations transmises via ces formulaires sont utilisées uniquement pour traiter la demande et assurer le suivi correspondant.

## 5. Newsletter et communications

Upnov IA ne propose pas nécessairement de newsletter à ce jour, mais pourra le faire ultérieurement.

Si une inscription à une newsletter ou à une communication régulière est proposée, elle reposera sur le consentement de l'utilisateur. L'utilisateur pourra se désinscrire à tout moment via le lien prévu à cet effet ou en écrivant à [dpo@upnov.com](mailto:dpo@upnov.com).

En l'absence d'inscription explicite à une newsletter, Upnov IA peut néanmoins recontacter une personne ayant effectué une demande sur le site dans le cadre du suivi commercial lié à cette demande.

## 6. Base légale des traitements

Les traitements reposent, selon les cas, sur :

* l'exécution de mesures précontractuelles, notamment lorsqu'une demande de devis, de diagnostic ou de programme est envoyée
* l'intérêt légitime d'Upnov IA à répondre aux demandes reçues et à assurer le suivi commercial de ses prospects et clients
* le consentement de l'utilisateur pour les cookies non nécessaires et, le cas échéant, pour l'inscription à une newsletter
* le respect d'obligations légales, comptables ou administratives lorsque cela est applicable

## 7. Destinataires des données

Les données sont destinées à Upnov IA.

Elles peuvent également être transmises à des prestataires techniques strictement nécessaires au fonctionnement du site et au traitement des demandes, notamment :

* OVH, pour l'hébergement du site
* Web3Forms, pour la gestion et la transmission des formulaires
* Google Analytics et Google Tag Manager, pour la mesure d'audience et la gestion des balises
* Microsoft Clarity, pour l'analyse de l'utilisation du site
* tarteaucitron.js, pour la gestion du consentement aux cookies

Ces prestataires interviennent uniquement dans le cadre des finalités décrites dans la présente politique.

## 8. Durée de conservation

Les données liées aux demandes de contact, de diagnostic, d'information, de programme ou de devis sont conservées pendant une durée maximale de 3 ans à compter du dernier contact avec la personne concernée.

Les données relatives aux clients peuvent être conservées pendant la durée nécessaire à la gestion de la relation commerciale, puis archivées pendant la durée légale applicable, notamment pour des raisons comptables ou fiscales.

Les données liées aux cookies sont conservées selon les durées indiquées dans l'outil de gestion du consentement aux cookies.

## 9. Cookies et traceurs

Le site utilise des cookies nécessaires à son fonctionnement ainsi que des cookies ou traceurs de mesure d'audience et d'analyse.

Les outils susceptibles d'utiliser des cookies ou traceurs sont notamment :

* Google Analytics
* Google Tag Manager
* Microsoft Clarity
* tarteaucitron.js

Les cookies non nécessaires au fonctionnement du site sont déposés uniquement après consentement de l'utilisateur.

L'utilisateur peut accepter, refuser ou modifier ses préférences à tout moment via le module de gestion du consentement disponible sur le site.

## 10. Microsoft Clarity

Le site peut utiliser Microsoft Clarity afin de comprendre comment les visiteurs interagissent avec le site, par exemple les pages consultées, les clics, le défilement ou les zones les plus consultées.

Ces informations sont utilisées pour améliorer l'expérience utilisateur, la lisibilité des pages et la performance du site.

Microsoft Clarity ne doit pas être utilisé pour collecter volontairement des données sensibles. Les champs de formulaires et données personnelles doivent, autant que possible, être masqués ou exclus des enregistrements.

## 11. Transferts hors Union européenne

Certains prestataires techniques, notamment Google, Microsoft ou Web3Forms, peuvent traiter certaines données en dehors de l'Union européenne.

Lorsque cela est nécessaire, Upnov IA veille à ce que des garanties appropriées soient mises en place conformément à la réglementation applicable.

## 12. Sécurité des données

Upnov IA met en œuvre des mesures raisonnables pour protéger les données personnelles contre l'accès non autorisé, la perte, l'altération ou la divulgation.

Toutefois, aucun système informatique ne permet de garantir une sécurité absolue.

## 13. Droits des utilisateurs

Conformément à la réglementation applicable, vous disposez des droits suivants sur vos données personnelles :

* droit d'accès
* droit de rectification
* droit d'effacement
* droit d'opposition
* droit à la limitation du traitement
* droit à la portabilité, lorsque ce droit est applicable
* droit de retirer votre consentement à tout moment lorsque le traitement repose sur le consentement

Pour exercer vos droits, vous pouvez écrire à :

[dpo@upnov.com](mailto:dpo@upnov.com)

En cas de difficulté, vous pouvez également adresser une réclamation auprès de la CNIL.

## 14. Modification de la politique de confidentialité

Upnov IA peut modifier la présente politique de confidentialité afin de tenir compte de l'évolution du site, des outils utilisés ou de la réglementation applicable.

La version applicable est celle publiée sur le site à la date de consultation.
`;

const PolitiqueConfidentialitePage = () => {
  return (
    <>
      <Helmet>
        <title>Politique de confidentialité — Upnov IA</title>
        <meta
          name="description"
          content="Politique de confidentialité d'Upnov IA : collecte, utilisation et protection de vos données personnelles sur www.upnov.com."
        />
        <meta name="robots" content="noindex, follow" />
        <meta property="og:title" content="Politique de confidentialité — Upnov IA" />
        <meta property="og:description" content="Politique de confidentialité d'Upnov IA : collecte, utilisation et protection de vos données personnelles sur www.upnov.com." />
        <meta property="og:url" content="https://www.upnov.com/politique-de-confidentialite" />
        <meta property="og:type" content="website" />
        <meta property="og:image" content="https://www.upnov.com/IA-et-conformite.webp" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
      </Helmet>

      <section className="bg-primary text-white py-12 md:py-16">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto">
            <h1 className="heading-lg mb-4">Politique de confidentialité</h1>
            <p className="text-indigo-200 text-sm">
              <Link to="/" className="hover:text-white transition-colors">Accueil</Link>
              {' '}&rsaquo;{' '}Politique de confidentialité
            </p>
          </div>
        </div>
      </section>

      <section className="section-padding bg-white">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto">
            <div className="prose prose-lg max-w-none">
              <ReactMarkdown
                remarkPlugins={[remarkGfm]}
                components={{
                  h1: () => null,
                  h2: ({ ...props }) => (
                    <h2 className="text-2xl font-bold text-gray-900 mt-10 mb-4" {...props} />
                  ),
                  h3: ({ ...props }) => (
                    <h3 className="text-xl font-bold text-gray-800 mt-8 mb-3" {...props} />
                  ),
                  p: ({ ...props }) => (
                    <p className="text-gray-700 mb-4 leading-relaxed" {...props} />
                  ),
                  a: ({ href, ...props }) => (
                    <a
                      href={href}
                      className="text-primary hover:underline transition-colors"
                      {...props}
                    />
                  ),
                  ul: ({ ...props }) => (
                    <ul className="list-disc pl-6 space-y-2 mb-6 text-gray-700" {...props} />
                  ),
                  ol: ({ ...props }) => (
                    <ol className="list-decimal pl-6 space-y-2 mb-6 text-gray-700" {...props} />
                  ),
                }}
              >
                {content}
              </ReactMarkdown>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default PolitiqueConfidentialitePage;
